import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import {
  Check,
  CheckCircle,
  FileText,
  LucideAngularModule,
  MessageSquare,
  Pencil,
} from 'lucide-angular';
import {
  AssignmentApiService,
  AssignmentResponse,
} from '../assignment/services/assignment-api.service';
import {
  ASSIGNMENT_THEME_COLOR,
  QuestionType,
} from '../assignment/services/assignment.models';
import { AssignmentInfoCardComponent } from '../../../common/components/assignment-info-card/assignment-info-card';
import { MetadataApiService } from '../../../common/services/metadata-api.service';
import { AmplitudeService } from '../../../common/analytics/amplitude.service';

@Component({
  selector: 'app-assignment-view',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, AssignmentInfoCardComponent],
  templateUrl: './assignment-view.html',
  styleUrl: './assignment-view.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentViewComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly apiService = inject(AssignmentApiService);
  private readonly metadataApiService = inject(MetadataApiService);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  protected readonly themeColor = ASSIGNMENT_THEME_COLOR;
  protected readonly QuestionType = QuestionType;

  protected readonly icons = {
    Check,
    CheckCircle,
    FileText,
    MessageSquare,
    Pencil,
  };

  private readonly assignment$$ =
    new BehaviorSubject<AssignmentResponse | null>(null);
  protected readonly assignment$ = this.assignment$$.asObservable();

  private readonly loading$$ = new BehaviorSubject<boolean>(true);
  protected readonly loading$ = this.loading$$.asObservable();

  private readonly error$$ = new BehaviorSubject<string | null>(null);
  protected readonly error$ = this.error$$.asObservable();

  /** Resolved tier1 subject category name */
  protected readonly tier1Name = signal('');

  /** Lookup map: tier1SubjectId → category name */
  private readonly tier1Map = signal<Map<string, string>>(new Map());

  private readonly OPTION_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.router.navigate(['/teacher/dashboard/assignments']);
      return;
    }

    this.metadataApiService
      .getMetadata()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((metadata) => {
        const map = new Map<string, string>();
        metadata.tier1Subjects.forEach((t) => map.set(t.id, t.category));
        this.tier1Map.set(map);
        this.resolveTier1Name();
      });

    this.apiService
      .getAssignment(id)
      .pipe(
        catchError(() => {
          this.error$$.next(
            'Failed to load assignment. It may not exist or you may not have access.',
          );
          return of(null);
        }),
        finalize(() => this.loading$$.next(false)),
      )
      .subscribe((assignment) => {
        this.assignment$$.next(assignment);
        this.resolveTier1Name();
      });
  }

  private resolveTier1Name(): void {
    const assignment = this.assignment$$.value;
    const map = this.tier1Map();
    if (assignment?.tier1SubjectId && map.size > 0) {
      this.tier1Name.set(map.get(assignment.tier1SubjectId) ?? '');
    }
  }

  protected getQuestionTypeLabel(type: QuestionType): string {
    switch (type) {
      case QuestionType.MULTIPLE_CHOICE:
        return 'Multiple choice';
      case QuestionType.SHORT_ANSWER:
        return 'Short answer';
      case QuestionType.ESSAY:
        return 'Essay';
      default:
        return 'Question';
    }
  }

  protected getQuestionTypeIcon(type: QuestionType): any {
    switch (type) {
      case QuestionType.MULTIPLE_CHOICE:
        return this.icons.CheckCircle;
      case QuestionType.SHORT_ANSWER:
        return this.icons.MessageSquare;
      case QuestionType.ESSAY:
        return this.icons.FileText;
      default:
        return this.icons.FileText;
    }
  }

  protected getOptionLetter(index: number): string {
    return this.OPTION_LETTERS[index] ?? String(index + 1);
  }

  // ============================================
  // TITLE EDITING
  // ============================================

  protected isEditingTitle = false;
  protected editingTitleValue = '';

  @ViewChild('titleInput') private titleInput?: ElementRef<HTMLTextAreaElement>;

  protected getTitle(assignment: AssignmentResponse): string {
    return (
      assignment.title?.trim() ||
      assignment.objective?.trim() ||
      'Untitled assignment'
    );
  }

  protected startTitleEdit(currentTitle: string): void {
    this.editingTitleValue = currentTitle;
    this.isEditingTitle = true;
    this.cdr.markForCheck();
    setTimeout(() => this.autoResizeTitleInput());
  }

  protected onTitleInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.editingTitleValue = textarea.value;
    this.autoResizeTitleInput();
  }

  private autoResizeTitleInput(): void {
    const el = this.titleInput?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }

  protected confirmTitleEdit(): void {
    const trimmed = this.editingTitleValue.trim();
    const assignment = this.assignment$$.value;
    if (trimmed && assignment) {
      this.apiService
        .updateAssignment(assignment.id, { title: trimmed })
        .pipe(
          map((updated) => {
            this.assignment$$.next(updated);
          }),
          catchError(() => of(undefined)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
    this.isEditingTitle = false;
    this.cdr.markForCheck();
  }

  protected cancelTitleEdit(): void {
    this.isEditingTitle = false;
    this.cdr.markForCheck();
  }

  protected onTestAsStudent(assignment: AssignmentResponse): void {
    if (assignment.shareCode) {
      this.amplitudeService.trackEvent('assignment_test_clicked', {
        assignment_id: assignment.id,
        source: 'assignment_view',
      });
      this.router.navigate(['/a', assignment.shareCode]);
    }
  }
}
